/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.22.0
 */
import type { FC } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { ERequestStatus } from '@/main/types/globalTypes';
import type { IApiError } from '@/main/types/globalTypes';
import { Button } from '@components/common';
import { ImgStatusError, ImgStatusSuccess } from '@components/common/Block/Img';

import { makeSelectEdmOperatorConnectionTranslation, makeSelectTranslationCommon } from '../../internationalization/intSelectors';
import Modal from '../../modal/components/Modal';
import { ModalCenterStyled } from '../../modal/components/styled/ModalCenterStyled';
import { modalSetStatusAction } from '../../modal/modalActions';
import { EModalStatus } from '../../modal/types/modalTypes';
import { EEdmErrorCode } from '../constants';
import { EmdConnectionErrorLstStyled } from '../styled/EmdConnectionStatusStyled';

export interface IProps {
    mode: ERequestStatus;
    title: string;
    mainText?: string;
    extraText?: string;
    requestError?: IApiError;
    onCloseModal?: () => void;
};

export const EdmRequestStatusModal: FC<IProps> = ({ mode, title, mainText, extraText, requestError, onCloseModal }) => {
    const dispatch = useDispatch();

    const tCommon = useSelector(makeSelectTranslationCommon());
    const { failure: tFailure } = useSelector(makeSelectEdmOperatorConnectionTranslation());

    const isSuccessMode = mode === ERequestStatus.success;

    const handleClick = () => {
        if (onCloseModal) {
            onCloseModal();
        } else {
            dispatch(modalSetStatusAction({
                status: EModalStatus.start,
            }));
        }
    };

    const setErrorMessage = () => {
        if (!!requestError?.validation?.length) {
            const errorText = requestError.validation.map(errorItem => {
                return (
                    <li key={errorItem.code}>
                        <Modal.Text key={errorItem.code}>
                            {errorItem.field}
                        </Modal.Text>
                    </li>
                );
            });

            return <EmdConnectionErrorLstStyled>{errorText}</EmdConnectionErrorLstStyled>;
        } else {
            let errorMessage = tCommon.error.defaultMessage;

            if (requestError?.code === EEdmErrorCode.dependentEdmWay
                || requestError?.code === EEdmErrorCode.dependentPaperWays) {
                errorMessage = tFailure.error.codes[EEdmErrorCode.dependentEdmWay];
            } else if (requestError?.code === EEdmErrorCode.edmConnectionNotFound) {
                errorMessage = tFailure.error.codes[EEdmErrorCode.edmConnectionNotFound]
            } else if (!!requestError?.code) {
                errorMessage = tCommon.error.codes[requestError.code] || tCommon.error.defaultMessage;
            };

            return (
                <Modal.Text>
                    {errorMessage}
                </Modal.Text>
            )
        };
    };

    return (
        <ModalCenterStyled>
            {isSuccessMode ? <ImgStatusSuccess /> : <ImgStatusError />}
            <Modal.Title>{title}</Modal.Title>
            {isSuccessMode && (
                <>
                    {!!mainText && <Modal.Text>{mainText}</Modal.Text>}
                    {!!extraText && <Modal.Text className='success-modal-extraText'>{extraText}</Modal.Text>}
                </>
            )}
            {!isSuccessMode && setErrorMessage()}
            <Modal.Control>
                <Button onClick={handleClick}>
                    {isSuccessMode ? tCommon.modal.good : tCommon.modal.accessibly}
                </Button>
            </Modal.Control>
        </ModalCenterStyled>
    );
};
